import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Carousel,
  Checkbox,
  Form,
  Input,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import {
  GlobalOutlined,
  MessageOutlined,
  DeploymentUnitOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login, clearError } from '../store/slices/authSlice';
import { fetchActivationInfo } from '../store/slices/activationSlice';
import { RootState, AppDispatch } from '../store/store';

const { Title, Text, Paragraph } = Typography;

const REMEMBERED_USERNAME_KEY = 'webstock_login_username';
const LOGIN_MARKETING_ENDPOINT = 'http://www.wktech.ltd/login_ads.php';

type LoginFormValues = {
  username: string;
  password: string;
  remember?: boolean;
};

type MarketingSlot = {
  id: string;
  badge: string;
  title: string;
  description: string;
  imageUrl?: string;
  linkText?: string;
  linkUrl?: string;
};

type MarketingFeatureIconKey = 'message' | 'global' | 'deployment';

type MarketingFeature = {
  id: string;
  iconKey: MarketingFeatureIconKey;
  title: string;
  value: string;
  suffix: string;
};

type MarketingHero = {
  badge: string;
  title: string;
  emphasis: string;
  description: string;
};

type LoginMarketingConfig = {
  hero: MarketingHero;
  slots: MarketingSlot[];
  features: MarketingFeature[];
};

const defaultMarketingSlots: MarketingSlot[] = [
  {
    id: 'overview',
    badge: '全域流通',
    title: '全球跨域商业生态',
    description: '打破地域与结算壁垒，无缝支持全球多店铺跨域统一管理与多币种实时换算组合。',
  },
  {
    id: 'analytics',
    badge: '极速供应链',
    title: '集中采购与智能调拨',
    description: '一站式整合所有终端需求，打通多店铺集中采购流水线，极速优化供应链成本与库存分布。',
  },
  {
    id: 'promotion',
    badge: '社交互联',
    title: 'WhatsApp 深度互联',
    description: '重塑全场景客户沟通链路，实现发票交付、订单状态与营销活动的 WhatsApp 实时自动触达。',
  },
];

const defaultFeatureItems: MarketingFeature[] = [
  {
    id: 'social',
    iconKey: 'message',
    title: '社交营销闭环',
    value: '直连',
    suffix: 'WhatsApp',
  },
  {
    id: 'network',
    iconKey: 'global',
    title: '跨域协同网络',
    value: '多币种',
    suffix: '通存通兑',
  },
  {
    id: 'supply-chain',
    iconKey: 'deployment',
    title: '集约化供应链',
    value: '统购',
    suffix: '智能分配',
  },
];

const defaultMarketingHero: MarketingHero = {
  badge: '全球智能商业',
  title: '打破管理孤岛',
  emphasis: '下一代商业生态',
  description:
    '远超传统 ERP 的一体化平台。原生融合 WhatsApp 社交连接力，打通多店跨域供应链调度与跨国多币种结算，让您的业务兼具集团全局视野与终端敏捷响应。',
};

const defaultMarketingConfig: LoginMarketingConfig = {
  hero: defaultMarketingHero,
  slots: defaultMarketingSlots,
  features: defaultFeatureItems,
};

const marketingIconMap: Record<MarketingFeatureIconKey, React.ReactNode> = {
  message: <MessageOutlined />,
  global: <GlobalOutlined />,
  deployment: <DeploymentUnitOutlined />,
};

const toNonEmptyString = (value: unknown, fallback = ''): string => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const trimmed = value.trim();
  return trimmed || fallback;
};

const normalizeMarketingConfig = (payload: unknown): LoginMarketingConfig => {
  const raw = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  const rawHero = (raw.hero && typeof raw.hero === 'object' ? raw.hero : {}) as Record<string, unknown>;

  const hero: MarketingHero = {
    badge: toNonEmptyString(rawHero.badge, defaultMarketingHero.badge),
    title: toNonEmptyString(rawHero.title, defaultMarketingHero.title),
    emphasis: toNonEmptyString(rawHero.emphasis, defaultMarketingHero.emphasis),
    description: toNonEmptyString(rawHero.description, defaultMarketingHero.description),
  };

  const slotsSource = Array.isArray(raw.slots) ? raw.slots : [];
  const slots = slotsSource
    .map((item, index) => {
      const slot = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      const fallback = defaultMarketingSlots[index] || defaultMarketingSlots[0];
      const normalized: MarketingSlot = {
        id: toNonEmptyString(slot.id, fallback.id),
        badge: toNonEmptyString(slot.badge, fallback.badge),
        title: toNonEmptyString(slot.title, fallback.title),
        description: toNonEmptyString(slot.description, fallback.description),
        imageUrl: toNonEmptyString(slot.imageUrl || slot.image_url, fallback.imageUrl),
        linkText: toNonEmptyString(slot.linkText || slot.link_text),
        linkUrl: toNonEmptyString(slot.linkUrl || slot.link_url),
      };
      return normalized;
    })
    .filter((item) => item.id && item.title);

  const featuresSource = Array.isArray(raw.features) ? raw.features : [];
  const features = featuresSource
    .map((item, index) => {
      const feature = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      const fallback = defaultFeatureItems[index] || defaultFeatureItems[0];
      const requestedIconKey = toNonEmptyString(feature.iconKey || feature.icon_key, fallback.iconKey);
      const iconKey: MarketingFeatureIconKey =
        requestedIconKey === 'message' || requestedIconKey === 'global' || requestedIconKey === 'deployment'
          ? requestedIconKey
          : fallback.iconKey;

      return {
        id: toNonEmptyString(feature.id, fallback.id),
        iconKey,
        title: toNonEmptyString(feature.title, fallback.title),
        value: toNonEmptyString(feature.value, fallback.value),
        suffix: toNonEmptyString(feature.suffix, fallback.suffix),
      };
    })
    .filter((item) => item.id && item.title);

  return {
    hero,
    slots: slots.length > 0 ? slots : defaultMarketingSlots,
    features: features.length > 0 ? features : defaultFeatureItems,
  };
};

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm<LoginFormValues>();
  const [marketingConfig, setMarketingConfig] = useState<LoginMarketingConfig>(defaultMarketingConfig);
  const [activeSlotId, setActiveSlotId] = useState(defaultMarketingConfig.slots[0]?.id || '');
  const carouselRef = useRef<any>(null);

  const { isAuthenticated, isLoading, error, user } = useSelector(
    (state: RootState) => state.auth,
  );

  const activeSlot =
    marketingConfig.slots.find((item) => item.id === activeSlotId) || marketingConfig.slots[0];
  const activeSlotIndex = Math.max(
    marketingConfig.slots.findIndex((item) => item.id === activeSlot?.id),
    0,
  );

  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(fetchActivationInfo());
      navigate('/', { replace: true });
    }
  }, [dispatch, isAuthenticated, navigate, user]);

  useEffect(() => {
    dispatch(clearError());
    const rememberedUsername = localStorage.getItem(REMEMBERED_USERNAME_KEY) || '';
    form.setFieldsValue({
      username: rememberedUsername,
      remember: Boolean(rememberedUsername),
    });
  }, [dispatch, form]);

  useEffect(() => {
    const controller = new AbortController();

    const loadMarketingConfig = async () => {
      try {
        const response = await fetch(LOGIN_MARKETING_ENDPOINT, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          cache: 'no-store',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`广告接口返回状态异常: ${response.status}`);
        }

        const result = await response.json();
        const nextConfig = normalizeMarketingConfig(result?.data ?? result);
        setMarketingConfig(nextConfig);
        setActiveSlotId((current) =>
          nextConfig.slots.some((item) => item.id === current) ? current : nextConfig.slots[0]?.id || '',
        );
      } catch (fetchError) {
        if ((fetchError as Error).name === 'AbortError') {
          return;
        }
        console.warn('[Login] 广告接口加载失败，回退到默认展示:', fetchError);
      }
    };

    loadMarketingConfig();

    return () => controller.abort();
  }, []);

  const onFinish = (values: LoginFormValues) => {
    if (values.remember) {
      localStorage.setItem(REMEMBERED_USERNAME_KEY, values.username);
    } else {
      localStorage.removeItem(REMEMBERED_USERNAME_KEY);
    }

    dispatch(
      login({
        username: values.username,
        password: values.password,
      }),
    );
  };

  const handleSelectSlot = (slotId: string, index: number) => {
    setActiveSlotId(slotId);
    if (carouselRef.current && typeof carouselRef.current.goTo === 'function') {
      carouselRef.current.goTo(index);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 20px',
        backgroundColor: '#fafbfc',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* 高科技视网膜网格背景与流体光晕 */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(37, 99, 235, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(37, 99, 235, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: '80vw',
          height: '80vw',
          background: 'radial-gradient(circle, rgba(0, 210, 255, 0.06) 0%, rgba(255,255,255,0) 60%)',
          top: '-20%',
          left: '-10%',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: '60vw',
          height: '60vw',
          background: 'radial-gradient(circle, rgba(179, 22, 210, 0.04) 0%, rgba(255,255,255,0) 60%)',
          bottom: '-10%',
          right: '-5%',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(255, 87, 87, 0.04) 0%, rgba(255,255,255,0) 50%)',
          top: '30%',
          left: '30%',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          width: 'min(1440px, 100%)',
          minHeight: 820,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.25fr) minmax(460px, 0.75fr)',
          borderRadius: 36,
          background: 'rgba(255, 255, 255, 0.75)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          boxShadow: '0 40px 80px rgba(15, 23, 42, 0.06), inset 0 2px 20px rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
        }}
      >
        {/* 左侧：科技与数据全景展示 */}
        <div
          style={{
            position: 'relative',
            padding: '56px 64px',
            borderRight: '1px solid rgba(226, 232, 240, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.6) 0%, rgba(255, 255, 255, 0.3) 100%)',
          }}
        >
          <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Title
              level={1}
              style={{
                margin: 0,
                fontSize: 56,
                lineHeight: 1.15,
                fontWeight: 900,
                letterSpacing: -1.5,
                color: '#0f172a',
              }}
            >
              {marketingConfig.hero.title}<br />
              <span style={{ 
                background: 'linear-gradient(120deg, #1D44E9 0%, #B316D2 40%, #FF5757 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'inline-block',
                paddingRight: '10px'
              }}>{marketingConfig.hero.emphasis}</span>
            </Title>

            <Paragraph
              style={{
                marginTop: 24,
                marginBottom: 48,
                maxWidth: 580,
                fontSize: 18,
                lineHeight: 1.8,
                color: '#475569',
                fontWeight: 400,
              }}
            >
              {marketingConfig.hero.description}
            </Paragraph>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 20,
                marginBottom: 48,
              }}
            >
              {marketingConfig.features.map((item) => (
                <Card
                  key={item.id}
                  bordered={false}
                  style={{
                    borderRadius: 24,
                    background: 'rgba(255, 255, 255, 0.85)',
                    border: '1px solid rgba(255, 255, 255, 1)',
                    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.04), inset 0 0 0 1px rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  bodyStyle={{ padding: '24px 22px' }}
                  onMouseEnter={(e) => {
                    const target = e.currentTarget as HTMLElement;
                    target.style.transform = 'translateY(-6px)';
                    target.style.boxShadow = '0 30px 60px rgba(15, 23, 42, 0.08), inset 0 0 0 1px rgba(255, 255, 255, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.currentTarget as HTMLElement;
                    target.style.transform = 'translateY(0)';
                    target.style.boxShadow = '0 20px 40px rgba(15, 23, 42, 0.04), inset 0 0 0 1px rgba(255, 255, 255, 0.5)';
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        background: 'linear-gradient(135deg, rgba(29, 68, 233, 0.08) 0%, rgba(179, 22, 210, 0.08) 100%)',
                        color: '#1D44E9',
                        borderRadius: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 28,
                        marginBottom: 20,
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      {marketingIconMap[item.iconKey]}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: '#0f172a',
                        marginBottom: 8,
                        letterSpacing: -0.5,
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        color: '#64748b',
                        fontWeight: 500,
                        lineHeight: 1.6,
                      }}
                    >
                      {item.value}
                      <span style={{ marginLeft: 4, opacity: 0.7 }}>
                        {item.suffix}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* 底部广告位 */}
            <Card
              bordered={false}
              style={{
                borderRadius: 28,
                background: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(255, 255, 255, 1)',
                boxShadow: '0 20px 40px rgba(15, 23, 42, 0.04)',
                overflow: 'hidden',
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
              }}
              bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ 
                padding: '16px 24px', 
                background: '#f8fafc', 
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                gap: 8
              }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f56' }} />
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ffbd2e' }} />
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#27c93f' }} />
                <span style={{ marginLeft: 8, fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  登录广告轮播
                </span>
              </div>

              <div style={{ padding: '24px 28px 28px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div
                  style={{
                    borderRadius: 24,
                    overflow: 'hidden',
                    background: '#eff6ff',
                    border: '1px solid rgba(191, 219, 254, 0.8)',
                    boxShadow: '0 18px 36px rgba(59, 130, 246, 0.12)',
                  }}
                >
                  <Carousel
                    ref={carouselRef}
                    autoplay
                    autoplaySpeed={5000}
                    dots={false}
                    afterChange={(index) => {
                      const nextSlot = marketingConfig.slots[index];
                      if (nextSlot) {
                        setActiveSlotId(nextSlot.id);
                      }
                    }}
                  >
                    {marketingConfig.slots.map((item) => (
                      <div key={item.id}>
                        <div
                          style={{
                            minHeight: 260,
                            position: 'relative',
                            background: item.imageUrl
                              ? `linear-gradient(135deg, rgba(15,23,42,0.18) 0%, rgba(29,68,233,0.08) 100%), url(${item.imageUrl}) center/cover no-repeat`
                              : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                            display: 'flex',
                            alignItems: 'flex-end',
                          }}
                        >
                          <div
                            style={{
                              width: '100%',
                              padding: '24px 24px 22px',
                              background: 'linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0.72) 100%)',
                              color: '#ffffff',
                            }}
                          >
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '6px 12px',
                                borderRadius: 999,
                                background: 'rgba(255,255,255,0.18)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                fontSize: 12,
                                fontWeight: 700,
                                marginBottom: 12,
                              }}
                            >
                              {item.badge}
                            </div>
                            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.6, marginBottom: 10 }}>
                              {item.title}
                            </div>
                            <div style={{ fontSize: 15, lineHeight: 1.8, opacity: 0.95, maxWidth: 560 }}>
                              {item.description}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Carousel>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginTop: 20,
                    marginBottom: 24,
                  }}
                >
                  {marketingConfig.slots.map((item, index) => {
                    const active = activeSlot?.id === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleSelectSlot(item.id, index)}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget as HTMLElement;
                          if (!activeSlot || activeSlot.id !== item.id) {
                            target.style.background = 'rgba(255, 255, 255, 1)';
                            target.style.transform = 'translateY(-2px)';
                            target.style.boxShadow = '0 6px 12px rgba(15, 23, 42, 0.08)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget as HTMLElement;
                          if (!activeSlot || activeSlot.id !== item.id) {
                            target.style.background = 'rgba(255, 255, 255, 0.8)';
                            target.style.transform = 'translateY(0)';
                            target.style.boxShadow = '0 4px 6px rgba(15, 23, 42, 0.04), inset 0 0 0 1px rgba(226, 232, 240, 0.8)';
                          }
                        }}
                        style={{
                          border: 'none',
                          background: active 
                            ? 'linear-gradient(135deg, #1D44E9 0%, #3b82f6 100%)' 
                            : 'rgba(255, 255, 255, 0.8)',
                          color: active ? '#ffffff' : '#64748b',
                          borderRadius: 999,
                          padding: '10px 20px',
                          fontSize: 14,
                          fontWeight: 700,
                          cursor: 'pointer',
                          boxShadow: active 
                            ? '0 10px 20px rgba(29, 68, 233, 0.25), inset 0 -2px 0 rgba(0, 0, 0, 0.1)' 
                            : '0 4px 6px rgba(15, 23, 42, 0.04), inset 0 0 0 1px rgba(226, 232, 240, 0.8)',
                          backdropFilter: active ? 'none' : 'blur(8px)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: active ? 'scale(1.05)' : 'scale(1)',
                        }}
                      >
                        {item.badge}
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                  <div style={{ flex: 1, minHeight: 104, display: 'flex', flexDirection: 'column' }}>
                    <div
                      style={{
                        width: 48,
                        height: 4,
                        borderRadius: 2,
                        background: 'linear-gradient(90deg, #1D44E9, #0ea5e9)',
                        marginBottom: 16,
                        flexShrink: 0,
                      }}
                    />
                    <Title
                      level={2}
                      style={{
                        color: '#0f172a',
                        fontSize: 24,
                        fontWeight: 800,
                        margin: '0 0 10px 0',
                        letterSpacing: -0.5,
                        flexShrink: 0,
                      }}
                    >
                      {activeSlot?.title}
                    </Title>
                    <Paragraph 
                      style={{ 
                        color: '#475569', 
                        fontSize: 15, 
                        lineHeight: 1.7, 
                        margin: 0, 
                        display: '-webkit-box', 
                        WebkitLineClamp: 2, 
                        WebkitBoxOrient: 'vertical', 
                        overflow: 'hidden' 
                      }}
                    >
                      {activeSlot?.description}
                    </Paragraph>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-end' }}>
                    {marketingConfig.slots.map((item, index) => (
                      <button
                        key={`${item.id}-dot`}
                        type="button"
                        onClick={() => handleSelectSlot(item.id, index)}
                        aria-label={`切换到广告 ${index + 1}`}
                        style={{
                          width: index === activeSlotIndex ? 26 : 10,
                          height: 10,
                          borderRadius: 999,
                          border: 'none',
                          background: index === activeSlotIndex ? '#1d4ed8' : '#cbd5e1',
                          cursor: 'pointer',
                          transition: 'all 0.25s ease',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* 右侧：极简科技感登录表单 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '50px 48px',
            background: 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 400,
            }}
          >
            <div style={{ marginBottom: 48 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <img
                  src="/wk-logo.jpg"
                  alt="WK Logo"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    objectFit: 'cover',
                    boxShadow: '0 8px 24px rgba(179, 22, 210, 0.25)',
                  }}
                />
                <Tag
                  style={{
                    borderRadius: 999,
                    padding: '6px 16px',
                    fontSize: 13,
                    fontWeight: 700,
                    background: 'rgba(29, 68, 233, 0.08)',
                    color: '#1D44E9',
                    border: '1px solid rgba(29, 68, 233, 0.15)',
                  }}
                >
                  {marketingConfig.hero.badge}
                </Tag>
              </div>
              <Paragraph
                style={{
                  margin: 0,
                  color: '#64748b',
                  fontSize: 16,
                }}
              >
                请输入系统管理账号完成身份安全验证
              </Paragraph>
            </div>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                style={{ 
                    marginBottom: 24, 
                    borderRadius: 12, 
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    background: '#fef2f2',
                    color: '#b91c1c'
                }}
                onClose={() => dispatch(clearError())}
              />
            )}

            <Spin spinning={isLoading}>
              <Form<LoginFormValues>
                form={form}
                name="login"
                onFinish={onFinish}
                layout="vertical"
                autoComplete="off"
                requiredMark={false}
              >
                <div style={{ marginBottom: 6, fontWeight: 600, color: '#334155', fontSize: 14 }}>唯一身份识别符</div>
                <Form.Item
                  name="username"
                  rules={[{ required: true, message: '请输入身份识别符' }]}
                >
                  <Input
                    placeholder="请输入账号 (User ID)"
                    size="large"
                    autoComplete="username"
                    style={{
                      height: 56,
                      borderRadius: 16,
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#0f172a',
                      fontSize: 16,
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.01)',
                      transition: 'all 0.3s',
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#1D44E9';
                        e.target.style.background = '#ffffff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(29, 68, 233, 0.15)';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'inset 0 2px 4px rgba(0,0,0,0.01)';
                    }}
                  />
                </Form.Item>

                <div style={{ marginBottom: 6, fontWeight: 600, color: '#334155', fontSize: 14 }}>核心访问密钥</div>
                <Form.Item
                  name="password"
                  rules={[{ required: true, message: '请输入访问密钥' }]}
                  style={{ marginBottom: 24 }}
                >
                  <Input.Password
                    placeholder="请输入密码 (Key)"
                    size="large"
                    autoComplete="current-password"
                    style={{
                      height: 56,
                      borderRadius: 16,
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#0f172a',
                      fontSize: 16,
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.01)',
                      transition: 'all 0.3s',
                    }}
                    onFocus={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.parentElement!.parentElement!.style.borderColor = '#B316D2';
                        target.parentElement!.parentElement!.style.background = '#ffffff';
                        target.parentElement!.parentElement!.style.boxShadow = '0 0 0 3px rgba(179, 22, 210, 0.15)';
                    }}
                     onBlur={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.parentElement!.parentElement!.style.borderColor = '#e2e8f0';
                        target.parentElement!.parentElement!.style.background = '#f8fafc';
                        target.parentElement!.parentElement!.style.boxShadow = 'inset 0 2px 4px rgba(0,0,0,0.01)';
                    }}
                  />
                </Form.Item>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 36,
                  }}
                >
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox style={{ color: '#64748b', fontWeight: 500 }}>记住设备凭证状态</Checkbox>
                  </Form.Item>
                  <Button type="link" style={{ paddingInline: 0, color: '#1D44E9', fontWeight: 600 }}>
                    忘记凭证？
                  </Button>
                </div>

                <style>{`
                  .tech-login-btn::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    border-radius: 16px;
                    background: linear-gradient(135deg, #00D2FF 0%, #1D44E9 30%, #B316D2 70%, #FF5757 100%);
                    z-index: -1;
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                  }
                  .tech-login-btn:hover::before {
                    box-shadow: 0 14px 30px rgba(29, 68, 233, 0.35);
                    transform: scaleY(1.05) scaleX(1.02);
                  }
                `}</style>
                <Form.Item style={{ marginBottom: 24 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    block
                    size="large"
                    className="tech-login-btn"
                    style={{
                      position: 'relative',
                      height: 60,
                      borderRadius: 16,
                      border: 'none',
                      fontSize: 18,
                      fontWeight: 800,
                      background: 'transparent',
                      color: '#ffffff',
                      zIndex: 1,
                      letterSpacing: 2,
                    }}
                  >
                    进入业务系统
                  </Button>
                </Form.Item>
              </Form>
            </Spin>

            <div
              style={{
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: 13,
                marginTop: 32,
                fontWeight: 500,
              }}
            >
              Enterprise Core v2.0 &copy; 2026 WK System
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
